import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";
import { AlertCircle, X } from "lucide-react";
import usePendingDemos from "../../hooks/usePendingDemos";

// Aviso al entrar: si el closer tiene demos pasadas sin resultado, se muestra una
// vez al día (flag en localStorage por usuario+fecha). Cada línea abre la ficha.
export default function PendingDemosNudge() {
  const { t } = useTranslation();
  const { pending, loading } = usePendingDemos();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  const flagKey = useMemo(() => {
    let me = "anon";
    try { me = jwtDecode(localStorage.getItem("token"))?.user_id || "anon"; } catch (_) {}
    const day = new Date().toISOString().slice(0, 10);
    return `pendingDemosNudge:${me}:${day}`;
  }, []);

  // Se lee UNA vez al montar: si al marcarlo como visto (efecto de abajo) se volviera a leer, el aviso
  // desaparecería en el mismo render en que sale.
  const [alreadyShown] = useState(() => { try { return localStorage.getItem(flagKey) === "1"; } catch { return false; } });
  const visible = !loading && !dismissed && !alreadyShown && pending.length > 0;

  // «Una vez al día» de verdad (15/10/2026): antes solo se marcaba al cerrarlo con la X o al pulsar una
  // demo, así que salía en cada pantalla que se abría (David: «sale esto todo el rato»). Ahora cuenta como
  // visto en cuanto aparece; hasta mañana no vuelve aunque se navegue sin cerrarlo.
  useEffect(() => {
    if (!visible) return;
    try { localStorage.setItem(flagKey, "1"); } catch (_) {}
  }, [visible, flagKey]);

  if (!visible) return null;

  const close = () => {
    try { localStorage.setItem(flagKey, "1"); } catch (_) {}
    setDismissed(true);
  };

  const go = (d) => {
    close();
    navigate("/contactos-listas", {
      state: {
        openContactId: d.lead_contact_id,
        expandSection: "demo",
        leadFallback: {
          contact_id: d.lead_contact_id,
          nombre: d.lead_nombre,
          phone_number: d.lead_telefono,
          sector: d.sector,
        },
      },
    });
  };

  return (
    <div className="fixed bottom-4 right-4 z-[60] w-80 max-w-[92vw] bg-white rounded-2xl shadow-2xl border border-red-100 p-4">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 text-red-600 font-black text-sm">
          <AlertCircle size={18} /> {t("demos.nudgeTitle")}
        </div>
        <button type="button" onClick={close} className="p-1 hover:bg-gray-100 rounded-lg"><X size={16} /></button>
      </div>
      <p className="text-xs text-gray-500 mb-2">{t("demos.nudgeBody", { count: pending.length })}</p>
      <ul className="space-y-1 max-h-56 overflow-y-auto">
        {pending.map((d) => (
          <li key={d.demo_id}>
            <button type="button" onClick={() => go(d)}
              disabled={!d.lead_contact_id}
              className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-red-50 text-sm font-medium text-gray-700 disabled:opacity-50">
              {d.lead_nombre || d.lead_telefono}
              <span className="block text-[11px] text-gray-400">
                {d.scheduled_at ? new Date(d.scheduled_at).toLocaleDateString("es-ES", { day: "numeric", month: "short" }) : ""}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
